import { useEffect } from 'react';
import { NativeModules, Platform } from 'react-native';
import { useDispatch } from 'react-redux';

import { i18nLoadRequest } from '../../store/i18n/i18nActions';
import LS from '../localStorageUtils';

const languages = [
	{ code: 'en', id: 1, label: 'English' },
	{ code: 'bg', id: 2, label: 'Български' },
	{ code: 'fr', id: 3, label: 'Français' },
	{ code: 'ru', id: 4, label: 'Русский' },
	{ code: 'tr', id: 5, label: 'Türkçe' },
];

const getDeviceLanguage = () => {
	const iosSettings = NativeModules.SettingsManager?.settings;
	const nativeLocale =
		Platform.OS === 'ios'
			? iosSettings?.AppleLocale || iosSettings?.AppleLanguages?.[0]
			: NativeModules.I18nManager?.localeIdentifier;
	const locale =
		nativeLocale ||
		(typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().locale : undefined);

	return typeof locale === 'string' ? locale.split(/[-_]/)[0].toLowerCase() : 'en';
};

const COUNTRY_TO_LANGUAGE: Record<string, string> = {
	BG: 'bg',
	FR: 'fr',
	RU: 'ru',
	TR: 'tr',
};

const GEO_LOOKUP_TIMEOUT_MS = 2500;

const getGeoLanguage = async (): Promise<string> => {
	try {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);

		const response = await fetch('https://ipapi.co/country/', { signal: controller.signal });
		clearTimeout(timeoutId);

		if (!response.ok) {
			return 'en';
		}

		const countryCode = (await response.text()).trim().toUpperCase();
		return COUNTRY_TO_LANGUAGE[countryCode] || 'en';
	} catch (error) {
		console.warn('[i18n] Geo language lookup failed, defaulting to en:', error);
		return 'en';
	}
};

export function useLanguageInit() {
	const dispatch = useDispatch();

	useEffect(() => {
		let cancelled = false;

		const initializeLanguage = async () => {
			const storedLang = Platform.OS === 'web' ? LS.get('lang') : await LS.getAsync('lang');
			const lang =
				storedLang || (Platform.OS === 'web' ? await getGeoLanguage() : getDeviceLanguage());
			const selectedLang = languages.find(l => l.code === lang) || languages[0];

			if (cancelled) {
				return;
			}

			if (!storedLang) {
				LS.set('lang', selectedLang.code);
			}

			console.log('[i18n] useLanguageInit', {
				platform: Platform.OS,
				storedLang,
				selectedLang,
			});

			dispatch(
				i18nLoadRequest({
					langId: selectedLang.id,
					langCode: selectedLang.code,
				}),
			);
		};

		initializeLanguage();

		return () => {
			cancelled = true;
		};
	}, [dispatch]);
}
